import { CommonModule, DOCUMENT } from '@angular/common';
import {
  Component,
  ElementRef,
  TemplateRef,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  viewChild
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { filter, map } from 'rxjs';

import { isNil } from '@gfe/core/utils/shared-utils';

import { FilterConnector } from '@core/models/entity/entity-selector-request.model';
import { ICONS } from '@core/services/icons/icons.service';

import { DropdownService } from '@shared/components/dropdown-singleton/dropdown.service';
import { FilterPopupComponent } from '@shared/components/filter-query-builder/filter-popup/filter-popup.component';

import { AddFilterDropdownComponent } from '../add-filter-dropdown/add-filter-dropdown.component';
import { FilterOperationsService } from '../filter-operations.service';
import {
  AvailableFilterField,
  DateFilterCondition,
  DurationFilterCondition,
  FilterCondition,
  FilterConfiguration,
  FilterDataType,
  FilterGroup,
  FilterOperation,
  LabelFilterCondition,
  NumberFilterCondition,
  SelectFilterCondition,
  TagFilterCondition,
  TextFilterCondition,
  UidFilterCondition
} from '../filter.models';

type FilterItem = FilterCondition | FilterGroup;

@Component({
  selector: 'app-complex-filter-builder',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    AddFilterDropdownComponent,
    FilterPopupComponent
  ],
  templateUrl: './complex-filter-builder.component.html',
  styleUrls: ['./complex-filter-builder.component.css']
})
export class ComplexFilterBuilderComponent {
  protected filterOpsService = inject(FilterOperationsService);
  private translate = inject(TranslateService);
  protected document = inject(DOCUMENT);
  protected dropdownService = inject(DropdownService);

  // Template references
  protected addFilterTemplate = viewChild<TemplateRef<unknown>>('addFilterTemplate');
  protected scrollContainer = viewChild<ElementRef<HTMLDivElement>>('scrollContainer');
  protected filterPopupTemplate = viewChild<TemplateRef<unknown>>('filterPopupTemplate');

  // Inputs
  filterConfiguration = input.required<FilterConfiguration>();
  availableFields = input.required<AvailableFilterField[]>();

  // Outputs
  filterUpdate = output<FilterConfiguration>();
  builderClosed = output<void>();

  // Signals
  protected localConfig = signal<FilterConfiguration>({
    rootGroup: {
      id: `root-${Date.now()}`,
      connector: FilterConnector.AND,
      conditions: []
    }
  });
  protected addFilterTargetGroup = signal<FilterGroup | null>(null);
  protected editingCondition = signal<{
    condition: FilterCondition;
    groupId: string;
  } | null>(null);
  protected showTopFade = signal<boolean>(false);
  protected showBottomFade = signal<boolean>(true);

  protected readonly ICONS = ICONS;
  protected readonly FilterConnector = FilterConnector;

  // Reactive language
  protected currentLang = toSignal(
    this.translate.onLangChange.pipe(
      filter((value) => !isNil(value)),
      map((value) => value.lang)
    ),
    { initialValue: this.translate.getCurrentLang() }
  );

  protected availableValuesForEditingCondition = computed(() => {
    const editing = this.editingCondition();
    if (!editing) return [];

    const fieldConfig = this.getFieldConfig(editing.condition);
    if (!fieldConfig) return [];

    if (
      fieldConfig.dataType === FilterDataType.LABELS ||
      fieldConfig.dataType === FilterDataType.TAGS ||
      fieldConfig.dataType === FilterDataType.SELECT ||
      fieldConfig.dataType === FilterDataType.MULTI_SELECT
    ) {
      const field = this.availableFields().find((f) => f.field === fieldConfig.field);
      return field?.availableValues || [];
    }

    return [];
  });

  constructor() {
    effect(() => {
      this.localConfig.set(this.filterConfiguration());
    });

    effect(() => {
      const container = this.scrollContainer()?.nativeElement;
      if (container) {
        setTimeout(() => {
          this.updateScrollFade(container);
        }, 0);
      }
    });
  }

  protected isGroup(item: FilterItem): item is FilterGroup {
    return 'conditions' in item && 'connector' in item;
  }

  protected isCondition(item: FilterItem): item is FilterCondition {
    return 'operation' in item && 'dataType' in item;
  }

  protected toggleGroupConnector(group: FilterGroup) {
    const newConnector =
      group.connector === FilterConnector.AND ? FilterConnector.OR : FilterConnector.AND;

    this.updateGroupConnector(group.id, newConnector);
  }

  protected addConditionToGroup(group: FilterGroup, event: Event) {
    const template = this.addFilterTemplate();
    if (!template) return;

    const target = event.currentTarget as HTMLElement;
    this.addFilterTargetGroup.set(group);

    this.dropdownService.show(target, template, {
      position: 'bottom-start',
      middlewares: ['offset', 'flip', 'shift'],
      offset: 8,
      useDefaultStyles: false,
      closeOnClickOutside: true,
      closeOnEscape: true
    });
  }

  protected addSubGroup(parentGroup: FilterGroup) {
    const newGroup: FilterGroup = {
      id: this.generateId(),
      connector: FilterConnector.AND,
      conditions: []
    };

    const updated = this.addItemToGroup(parentGroup.id, newGroup);
    if (updated) {
      this.localConfig.set(updated);
    }
  }

  protected removeItem(itemId: string, parentGroupId: string) {
    const updated = this.removeItemFromGroup(parentGroupId, itemId);
    if (updated) {
      this.localConfig.set(updated);
    }
  }

  protected editCondition(
    condition: FilterCondition,
    groupId: string,
    element: HTMLElement
  ) {
    this.editingCondition.set({ condition, groupId });

    const template = this.filterPopupTemplate();
    if (!template) return;

    this.dropdownService.show(element, template, {
      position: 'bottom-start',
      middlewares: ['offset', 'flip', 'shift'],
      offset: 8,
      useDefaultStyles: false,
      closeOnClickOutside: true,
      closeOnEscape: true
    });
  }

  protected updateCondition(condition: FilterCondition) {
    const editing = this.editingCondition();
    if (!editing) return;

    const updated = this.replaceConditionInGroup(editing.groupId, condition);
    if (updated) {
      this.localConfig.set(updated);
      this.editingCondition.set(null);
    }
  }

  protected handleAddFilter(condition: FilterCondition) {
    const targetGroup = this.addFilterTargetGroup();
    if (!targetGroup) return;

    const updated = this.addItemToGroup(targetGroup.id, condition);
    if (updated) {
      this.localConfig.set(updated);
    }

    this.dropdownService.close();
  }

  protected apply() {
    this.filterUpdate.emit(this.localConfig());
    this.builderClosed.emit();
  }

  protected cancel() {
    this.builderClosed.emit();
  }

  protected getConditionSummary(condition: FilterCondition): string {
    const operationConfig = this.filterOpsService.getOperationConfig(condition.operation);
    const fieldConfig = this.getFieldConfig(condition);
    const fieldLabel = fieldConfig?.label || condition.field;
    const summary = `${fieldLabel} ${operationConfig.label}`;

    // Handle Boolean filters (no value needed)
    if (
      condition.dataType === FilterDataType.BOOLEAN ||
      condition.operation === 'IS_EMPTY' ||
      condition.operation === 'IS_NOT_EMPTY' ||
      condition.operation === 'IS_TRUE' ||
      condition.operation === 'IS_FALSE'
    ) {
      return summary;
    }

    // Handle Number filters
    if (condition.dataType === FilterDataType.NUMBER) {
      const numCondition = condition as NumberFilterCondition;
      if (
        condition.operation === 'BETWEEN' &&
        numCondition.minValue &&
        numCondition.maxValue
      ) {
        return `${summary}: ${numCondition.minValue} - ${numCondition.maxValue}`;
      } else if (numCondition.value !== undefined && numCondition.value !== null) {
        return `${summary}: ${numCondition.value}`;
      }
    }

    // Handle Date filters
    if (condition.dataType === FilterDataType.DATE) {
      const dateCondition = condition as DateFilterCondition;
      if (condition.operation === 'IS_RELATIVE' && dateCondition.relativeDate) {
        const relativeDateLabel = this.translate.instant(
          `filters.relative_dates.${dateCondition.relativeDate.toLowerCase()}`
        );
        return `${summary}: ${relativeDateLabel}`;
      } else if (
        condition.operation === 'BETWEEN' &&
        dateCondition.minValue &&
        dateCondition.maxValue
      ) {
        return `${summary}: ${dateCondition.minValue} - ${dateCondition.maxValue}`;
      } else if (dateCondition.value) {
        return `${summary}: ${dateCondition.value}`;
      }
    }

    // Handle Duration filters
    if (condition.dataType === FilterDataType.DURATION) {
      const durationCondition = condition as DurationFilterCondition;
      if (
        condition.operation === 'BETWEEN' &&
        durationCondition.minValue &&
        durationCondition.maxValue
      ) {
        return `${summary}: ${durationCondition.minValue} - ${durationCondition.maxValue}`;
      } else if (durationCondition.value) {
        return `${summary}: ${durationCondition.value}`;
      }
    }

    // Handle Label filters
    if (condition.dataType === FilterDataType.LABELS) {
      const labelCondition = condition as LabelFilterCondition;
      const labelKey = labelCondition.labelKey || '';

      // Exists operations carry no value
      if (
        condition.operation === FilterOperation.HAS_LABEL ||
        condition.operation === FilterOperation.NOT_HAS_LABEL
      ) {
        return `${summary}: ${labelKey}`;
      } else if (
        (condition.operation === 'LABEL_IN' || condition.operation === 'LABEL_NOT_IN') &&
        labelCondition.labelValues &&
        labelCondition.labelValues.length > 0
      ) {
        return `${summary}: ${labelKey} [${labelCondition.labelValues.join(', ')}]`;
      } else if (
        (condition.operation === 'LABEL_EQUALS' ||
          condition.operation === 'LABEL_NOT_EQUALS') &&
        labelCondition.labelValue
      ) {
        return `${summary}: ${labelKey} = ${labelCondition.labelValue}`;
      }
    }

    // Handle Tag filters
    if (condition.dataType === FilterDataType.TAGS) {
      const tagCondition = condition as TagFilterCondition;
      if (tagCondition.tagNames && tagCondition.tagNames.length > 0) {
        return `${summary}: [${tagCondition.tagNames.join(', ')}]`;
      }
    }

    // Handle UID filters
    if (
      condition.dataType === FilterDataType.UID ||
      condition.dataType === FilterDataType.UID_LIST
    ) {
      const uidCondition = condition as UidFilterCondition;
      if (uidCondition.values && uidCondition.values.length > 0) {
        const displayValues =
          uidCondition.values.length > 3
            ? `${uidCondition.values.slice(0, 3).join(', ')} +${uidCondition.values.length - 3}`
            : uidCondition.values.join(', ');
        return `${summary}: [${displayValues}]`;
      } else if (uidCondition.value) {
        return `${summary}: ${uidCondition.value}`;
      }
    }

    // Handle Select and Multi-Select filters
    if (
      condition.dataType === FilterDataType.SELECT ||
      condition.dataType === FilterDataType.MULTI_SELECT
    ) {
      const selectCondition = condition as SelectFilterCondition;
      if (selectCondition.values && selectCondition.values.length > 0) {
        const displayValues =
          selectCondition.values.length > 3
            ? `${selectCondition.values.slice(0, 3).join(', ')} +${selectCondition.values.length - 3}`
            : selectCondition.values.join(', ');
        return `${summary}: [${displayValues}]`;
      }
    }

    // Handle Text filters (including LINK, TEXT, MULTILINE_TEXT)
    if (
      condition.dataType === FilterDataType.TEXT ||
      condition.dataType === FilterDataType.MULTILINE_TEXT ||
      condition.dataType === FilterDataType.LINK
    ) {
      const textCondition = condition as TextFilterCondition;
      if (textCondition.value) {
        return `${summary}: "${textCondition.value}"`;
      }
    }

    // Fallback
    return summary;
  }

  protected getFieldConfig(condition: FilterCondition): AvailableFilterField | undefined {
    return this.availableFields().find((f) =>
      f.dataType === FilterDataType.LABELS
        ? f.labelName === (condition as LabelFilterCondition).labelKey
        : (f.dbColumnName || f.field) === condition.field
    );
  }

  protected trackByItem(item: FilterItem): string {
    return this.isGroup(item) ? item.id : (item as FilterCondition).id;
  }

  protected onScroll(event: Event): void {
    const target = event.target as HTMLElement;
    this.updateScrollFade(target);
  }

  private updateScrollFade(container: HTMLElement): void {
    const scrollTop = container.scrollTop;
    const scrollHeight = container.scrollHeight;
    const clientHeight = container.clientHeight;

    this.showTopFade.set(scrollTop > 10);

    this.showBottomFade.set(scrollTop + clientHeight < scrollHeight - 10);
  }

  private updateGroupConnector(groupId: string, connector: FilterConnector): void {
    const config = this.localConfig();
    const updated = this.updateGroupInTree(config.rootGroup, groupId, (group) => ({
      ...group,
      connector
    }));

    if (updated) {
      this.localConfig.set({
        ...config,
        rootGroup: updated as FilterGroup
      });
    }
  }

  private addItemToGroup(groupId: string, item: FilterItem): FilterConfiguration | null {
    const config = this.localConfig();
    const updated = this.updateGroupInTree(config.rootGroup, groupId, (group) => ({
      ...group,
      conditions: [...group.conditions, item]
    }));

    if (updated) {
      return {
        ...config,
        rootGroup: updated as FilterGroup
      };
    }

    return null;
  }

  private removeItemFromGroup(groupId: string, itemId: string): FilterConfiguration | null {
    const config = this.localConfig();
    const updated = this.updateGroupInTree(config.rootGroup, groupId, (group) => ({
      ...group,
      conditions: group.conditions.filter((item) => {
        const id = this.isGroup(item) ? item.id : (item as FilterCondition).id;
        return id !== itemId;
      })
    }));

    if (updated) {
      return {
        ...config,
        rootGroup: updated as FilterGroup
      };
    }

    return null;
  }

  private replaceConditionInGroup(
    groupId: string,
    condition: FilterCondition
  ): FilterConfiguration | null {
    const config = this.localConfig();
    const updated = this.updateGroupInTree(config.rootGroup, groupId, (group) => ({
      ...group,
      conditions: group.conditions.map((item) => {
        if (this.isCondition(item) && item.id === condition.id) {
          return condition;
        }
        return item;
      })
    }));

    if (updated) {
      return {
        ...config,
        rootGroup: updated as FilterGroup
      };
    }

    return null;
  }

  private updateGroupInTree(
    group: FilterGroup,
    targetId: string,
    updateFn: (group: FilterGroup) => FilterGroup
  ): FilterGroup | null {
    if (group.id === targetId) {
      return updateFn(group);
    }

    const updatedConditions = group.conditions.map((item) => {
      if (this.isGroup(item)) {
        const updated = this.updateGroupInTree(item, targetId, updateFn);
        return updated || item;
      }
      return item;
    });

    const hasChanges = updatedConditions.some(
      (item, index) => item !== group.conditions[index]
    );

    if (hasChanges) {
      return {
        ...group,
        conditions: updatedConditions
      };
    }

    return null;
  }

  private generateId(): string {
    return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
  }
}
